import { Link } from '@tanstack/react-router'
import { useRooms } from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { IconCheck, IconPlus, cn } from '@lb/ui'

import { http } from '../../lib/client'
import { useContentLocale } from '../../lib/content-locale'

/**
 * The rooms, in the sidebar (`community.jsx` — the nested rail under Community).
 *
 * ── Why this belongs in the rail rather than only on the page ─────────────
 * The design nests the rooms under the Community nav item and splits them:
 * **All rooms**, then **YOUR ROOMS** with a tick, then **DISCOVER** with a plus.
 * Rooms were reachable only through the landing page's grid, so moving between
 * two rooms meant going back out to the list and in again — on the one surface
 * where a member is most likely to be checking several.
 *
 * The split is also the only place the product says *which rooms are yours*
 * without opening each one.
 *
 * ── It renders only on Community routes ──────────────────────────────────
 * Passed through the shell's `aside` slot by the root layout, so the rail does
 * not carry a room list on the Library. The query is shared with the landing
 * page, so arriving from it costs nothing.
 */
export function CommunityRoomNav({ activeRoomId }: { activeRoomId?: string }) {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const rooms = useRooms(http, locale)

  const items = rooms.data?.items ?? []
  if (items.length === 0) return null

  const joined = items.filter((room) => room.joined)
  const discover = items.filter((room) => !room.joined)

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/community"
        className="flex min-h-[38px] items-center gap-2.5 rounded-[10px] px-3 text-[13px] font-semibold text-ink-soft transition-colors hover:bg-canvas hover:text-ink"
      >
        {t('community.allRooms')}
      </Link>

      {joined.length > 0 ? (
        <Group title={t('community.yourRooms')}>
          {joined.map((room) => (
            <RoomLink key={room.id} id={room.id} name={room.name} active={room.id === activeRoomId}>
              <IconCheck size={13} title={null} className="flex-none text-ink" />
            </RoomLink>
          ))}
        </Group>
      ) : null}

      {discover.length > 0 ? (
        <Group title={t('community.discover')}>
          {discover.map((room) => (
            <RoomLink key={room.id} id={room.id} name={room.name} active={room.id === activeRoomId}>
              {/*
               * A plus, not a join button. The design marks these as joinable;
               * actually joining happens in the room, where the House Rules and
               * the room's own description are in front of somebody. A one-tap
               * join from a rail is a membership nobody read anything about.
               */}
              <IconPlus size={13} title={null} className="flex-none text-muted" />
            </RoomLink>
          ))}
        </Group>
      ) : null}
    </div>
  )
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 px-3 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate">
        {title}
      </p>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  )
}

function RoomLink({
  id,
  name,
  active,
  children,
}: {
  id: string
  name: string
  active: boolean
  children: React.ReactNode
}) {
  return (
    <Link
      to="/community/$roomId"
      params={{ roomId: id }}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex min-h-[36px] items-center gap-2 rounded-[10px] px-3 text-[13px] transition-colors',
        active ? 'bg-ink text-cream' : 'text-ink-soft hover:bg-canvas hover:text-ink',
      )}
    >
      {/* The name truncates; the marker never does. A rail that drops the tick
          to fit a long room name stops saying which rooms are yours. */}
      <span className="min-w-0 flex-1 truncate">{name}</span>
      {children}
    </Link>
  )
}
